import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
    Typography,
    Tooltip
} from '@mui/material';
import { DataGridPro, GridColDef, GridColumnVisibilityModel, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useEnforceMaxCPCMutation } from 'src/context/api/liveBidding/api';
import Iconify from 'src/components/iconify';

type EnforceMaxCpcModalProps = {
    open: boolean;
    onClose: () => void;
    selectedRows: any[];
    onFinish: (localRows: any[], setResponse: (res: any[]) => void) => Promise<void>;
};

const EnforceMaxCpcModal: React.FC<EnforceMaxCpcModalProps> = ({
    open,
    onClose,
    selectedRows,
    onFinish
}) => {
    const [localRows, setLocalRows] = useState<any[]>([]);
    const [enforceMaxCPC, { isLoading, error, data }] = useEnforceMaxCPCMutation();
    // Réponse de l'API contenant les statuts (error ou success)
    const [errorEnforceMaxCPC, setErrorEnforeMaxCPC] = useState<any[]>([]);
    // Mapping keyword_id -> message pour les erreurs et succès
    const [errorRowsMapping, setErrorRowsMapping] = useState<{ [key: string]: string }>({});
    const [successRowsMapping, setSuccessRowsMapping] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);

useEffect(() => {
    if (!Array.isArray(selectedRows)) {
        setLocalRows([]);
        return;
    }

    setLocalRows(
        selectedRows.map((row) => ({
            ...row,
            campaign_id: row.campaign_id,
            account_id: row.account_id,
            max_cpc_to_enforce:
                row.max_cpc != null
                    ? row.max_cpc.toString()
                    : row.max_cpc_portfolio_startegy != null
                        ? row.max_cpc_portfolio_startegy.toString()
                        : '',
            _error: false
        }))
    );
}, [selectedRows]);


    // Mettre à jour errorRowsMapping et successRowsMapping dès réception de la réponse d'API
    useEffect(() => {
        if (errorEnforceMaxCPC && Array.isArray(errorEnforceMaxCPC)) {
            const errorMap: { [key: string]: string } = {};
            const successMap: { [key: string]: string } = {};
            errorEnforceMaxCPC.forEach((item: any) => {
                if (item.status === 'error') {
                    errorMap[item.keyword_id] = item.message;
                } else if (item.status === 'success') {
                    successMap[item.keyword_id] = item.message;
                }
            });
            setErrorRowsMapping(errorMap);
            setSuccessRowsMapping(successMap);
        }
    }, [errorEnforceMaxCPC]);

    const columns: GridColDef[] = [
        {
            field: 'platform',
            headerName: 'Platform',
            flex: 1,
            renderCell: (params) => <span>{params.value}</span>
        },
        {
            field: 'account_name',
            headerName: 'Account',
            flex: 1,
            renderCell: (params) => <span>{params.value}</span>
        },
        {
            field: 'campaign_names',
            headerName: 'Campaign',
            flex: 1,
            renderCell: (params) => <span>{params.value}</span>
        },
        {
            field: 'keyword',
            headerName: 'Keyword',
            flex: 1,
            renderCell: (params) => <span>{params.value ? params.value : '-'}</span>
        },
        {
            field: 'match_type',
            headerName: 'Match Type',
            flex: 1,
            renderCell: (params) => <span>{params.value ? params.value : '-'}</span>
        },
        {
            field: 'ad_group_names',
            headerName: 'Ad Group',
            flex: 1,
            renderCell: (params) => <span>{params.value ? params.value : '-'}</span>
        },
        {
            field: 'max_cpc_to_enforce',
            headerName: 'Max CPC to enforce',
            width: 200,
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                const rowId = params.id;
                const rowData = params.row;
                const hasError = rowData._error;
                const minValue = rowData.platform === 'Bing Ads' ? 0.05 : 0.01;

                const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                    const newInputVal = event.target.value;
                    setLocalRows((prev) =>
                        prev.map((r) =>
                            r.id === rowId ? { ...r, max_cpc_to_enforce: newInputVal, _error: false } : r
                        )
                    );
                };

                return (
                    <Box sx={{ mt: 0.7 }}>
                        <TextField
                            variant="outlined"
                            size="small"
                            disabled={errorEnforceMaxCPC?.length !== 0}
                            type="text"
                            value={rowData.max_cpc_to_enforce}
                            onChange={handleChange}
                            error={hasError}
                            fullWidth
                            inputProps={{ min: minValue }}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': hasError
                                    ? { borderColor: '#ff5630!important' }
                                    : {}
                            }}
                        />
                    </Box>
                );
            }
        },
        {
            field: 'response_info',
            headerName: 'Info',
            width: 80,
            renderCell: (params: GridRenderCellParams) => {
                const id = params.row.keyword_id;
                const errorMsg = errorRowsMapping[id];
                const successMsg = successRowsMapping[id];

                if (errorMsg) {
                    return (
                        <Tooltip title={errorMsg} sx={{ mt: 2 }}>
                            <Iconify icon="mdi:times" />
                        </Tooltip>
                    );
                } else if (successMsg) {
                    return (
                        <Tooltip title={successMsg} sx={{ mt: 2 }}>
                            <Iconify icon="mdi:check" />
                        </Tooltip>
                    );
                }
                return null;
            }
        }
    ];

    useEffect(() => {
        if (errorEnforceMaxCPC && Array.isArray(errorEnforceMaxCPC)) {
            const errorMap: { [key: string]: string } = {};
            const successMap: { [key: string]: string } = {};
            errorEnforceMaxCPC.forEach((item: any) => {
                if (item.status === 'error') {
                    errorMap[item.keyword_id] = item.message;
                } else if (item.status === 'success') {
                    successMap[item.keyword_id] = item.message;
                }
            });
            setErrorRowsMapping(errorMap);
            setSuccessRowsMapping(successMap);
        }
    }, [errorEnforceMaxCPC]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>();

    useEffect(() => {
        setColumnVisibilityModel(prev => ({
            ...prev,
            response_info: errorEnforceMaxCPC?.length !== 0
        }));
    }, [errorEnforceMaxCPC]);

    const handleSave = async () => {
        let hasError = false;

        const updatedRows = localRows.map((row) => {
            const rawValue = row.max_cpc_to_enforce?.toString().trim() || '';
            const normalized = rawValue.replace(',', '.');
            const numericVal = parseFloat(normalized);
            const minValue = row.platform === 'Bing Ads' ? 0.05 : 0.01;

            if (!rawValue || isNaN(numericVal) || numericVal < minValue) {
                hasError = true;
                return { ...row, _error: true };
            }

            return {
                ...row,
                max_cpc_to_enforce: numericVal,
                _error: false
            };
        });

        setLocalRows(updatedRows);

        if (hasError) {
            return;
        }

        setLoading(true);
        await onFinish(updatedRows, (res) => {
            setErrorEnforeMaxCPC(res);
            setLoading(false); // Stop loading here
        });
    };



    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                <Box sx={{ mb: 0.5 }}>
                    <Typography>
                        Max CPC to enforce ({localRows.length} {localRows.length === 1 ? 'item' : 'items'})
                    </Typography>
                </Box>
                <Typography variant="overline" color="textSecondary" sx={{ textTransform: 'none', fontSize: 14 }}>
                    List of all your activations for which you can enforce your Max CPC
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ mt: 1 }}>
                <Box
                    sx={{
                        height: 400,
                        width: '100%',
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                            outline: 'none !important'
                        },
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick
                        disableDensitySelector
                        autoHeight
                        rows={localRows}
                        columns={columns}
                        columnVisibilityModel={columnVisibilityModel}
                        getRowId={(row) => row.id}
                        getRowClassName={(params) => {
                            if (errorRowsMapping[params.row.keyword_id]) {
                                return 'error-row';
                            } else if (successRowsMapping[params.row.keyword_id]) {
                                return 'success-row';
                            }
                            return '';
                        }}
                        sx={{
                            '& .error-row': {
                                backgroundColor: 'rgba(255, 0, 0, 0.2)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 0, 0, 0.2)!important',
                                },
                            },
                            '& .success-row': {
                                backgroundColor: 'rgba(0, 128, 0, 0.2)',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 128, 0, 0.2)!important',
                                },
                            }
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={errorEnforceMaxCPC?.length !== 0}>
                    {loading ? 'Enforcing...' : 'Enforce'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EnforceMaxCpcModal;
